import { IonContent } from '@ionic/react';
import { AnimatePresence, useReducedMotion } from 'motion/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import VideoIcon from '@/assets/video.svg?react';
import { UiTypography } from '@/components/ui/Typography';
import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { useOnboarding } from '@/pages/Onboarding/context';
import {
  OwnAiTitle,
  OwnAiTitleHighlight,
} from '@/pages/Onboarding/Layouts/OwnAISecond/components/Title';
import { useLayoutFormSelections } from '@/pages/Onboarding/Layouts/OwnAISecond/hooks/useLayoutFormSelections';
import { SEND_PHOTOS_OPTIONS } from '@/pages/Onboarding/options/SendPhotos/SEND_PHOTOS_OPTIONS';
import { SendPhotosOptionsType } from '@/pages/Onboarding/options/SendPhotos/types';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { LayoutDataType } from '../../types';

import {
  SendVideoAnswerItemStyles,
  SendVideoAnswersStyles,
  SendVideoIconStyles,
  SendVideoStyles,
} from './SendVideo.styles';

export const ScreenSendVideo: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'SendVideo',
      });
    }
  }, []);

  const shouldAnimate = webCampaign.isAnimated();
  const [showElements, setShowElements] = React.useState<boolean>(true);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const sendVideos = form.watch('send_videos');

  const { ageGeneration, bodyShape, hairColor } = useLayoutFormSelections();

  const handleSelect = (
    option: OnbOptionType<LayoutDataType['send_videos']>,
  ) => {
    form.setValue('send_videos', option.id);
    userService.createOrUpdate({
      onboarding_data: {
        send_videos: option.title,
      },
    });

    if (shouldAnimate) {
      setTimeout(() => {
        if (shouldAnimate) {
          setShowElements(false);
        }
        nextStepTimeout(wizard.nextStep);
      }, 200);
    } else {
      nextStepTimeout(wizard.nextStep);
    }
  };

  return (
    <SendVideoStyles
      background="dark-violet"
      bgImage={
        <video
          poster={`https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai-second-video/${ageGeneration}/${bodyShape}/${hairColor}/send_photos_3.png`}
          autoPlay
          muted
          loop
          playsInline
          controls={false}
        >
          <source
            src={`https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai-second-video/${ageGeneration}/${bodyShape}/${hairColor}/video.webm`}
            type="video/webm"
          />
          <source
            src={`https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai-second-video/${ageGeneration}/${bodyShape}/${hairColor}/video.mp4`}
            type="video/mp4"
          />
        </video>
      }
    >
      <IonContent>
        <div>
          <SendVideoIconStyles>
            <VideoIcon />
          </SendVideoIconStyles>

          <OwnAiTitle animate={shouldAnimate} show={showElements}>
            Would you like to receive{' '}
            <OwnAiTitleHighlight>videos</OwnAiTitleHighlight>?
          </OwnAiTitle>

          <SendVideoAnswersStyles>
            {SEND_PHOTOS_OPTIONS.map((option) => (
              <Option
                key={option.id}
                option={option}
                isSelected={sendVideos === option.id}
                onClick={handleSelect}
                show={showElements}
                animate={shouldAnimate}
              />
            ))}
          </SendVideoAnswersStyles>
        </div>
      </IonContent>
    </SendVideoStyles>
  );
};

function Option({
  isSelected,
  onClick,
  option,
  show,
  animate,
}: {
  isSelected: boolean;
  onClick: (option: SendPhotosOptionsType[number]) => void;
  option: SendPhotosOptionsType[number];
  show: boolean;
  animate?: boolean;
}) {
  const shouldReduceMotion = useReducedMotion();
  const canAnimate = animate && !shouldReduceMotion;

  if (canAnimate) {
    const scale = isSelected ? 1.03 : 0.97;

    return (
      <AnimatePresence>
        {show && (
          <SendVideoAnswerItemStyles
            isSelected={isSelected}
            onClick={() => onClick(option)}
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1, scale }}
            exit={{ x: 100, opacity: 0 }}
            answer={option.id}
          >
            <UiTypography variant="title-medium" className="answer-title">
              {option.title}
            </UiTypography>
          </SendVideoAnswerItemStyles>
        )}
      </AnimatePresence>
    );
  }

  const scale = isSelected ? 1.03 : 1;

  return (
    <SendVideoAnswerItemStyles
      key={option.id}
      answer={option.id}
      isSelected={isSelected}
      onClick={() => onClick(option)}
      initial={{
        scale,
      }}
      animate={{
        scale,
      }}
    >
      <UiTypography variant="title-medium" className="answer-title">
        {option.title}
      </UiTypography>
    </SendVideoAnswerItemStyles>
  );
}
